import { MaintenancePointApi } from 'api/MaintenancePointApi'
import { Button } from 'components/Button/Button'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import {
  MAINTENANCE_POINT_TYPE_OPTIONS,
  maintenancePointMapIconId,
} from 'pages/Assets/maintenance/maintenancePointUtils'
import AssetGeoPointMap from 'pages/Assets/shared/AssetGeoPointMap'
import { useFacilityDialogGeoContext } from 'pages/Assets/shared/useFacilityDialogGeoContext'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSuppressNotificationSandwich } from 'hooks/useSuppressNotificationSandwich'
import { NOTIFICATION_SANDWICH_SUPPRESS } from 'utils/notificationSandwichGate'
import Modal from 'react-modal'
import { IMaintenancePoint, MaintenancePointType } from 'utils/interfaces'

import style from './AddMaintenancePointModal.module.css'

const defaultPoint: IMaintenancePoint = {
  id: 0,
  name: '',
  type: 'waste_water_drain',
  facility_id: 0,
  facility_index: null,
}

const AddMaintenancePointModal = ({
  isOpen,
  onClose,
  onSave,
  facilityId,
  points: _points,
  pointExisting,
}: {
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  facilityId: number
  points: IMaintenancePoint[]
  pointExisting?: IMaintenancePoint
}) => {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const isEditMode = Boolean(pointExisting)
  useSuppressNotificationSandwich(isOpen, NOTIFICATION_SANDWICH_SUPPRESS.MAINTENANCE_POINT_GEO)

  const [tempPoint, setTempPoint] = useState<IMaintenancePoint>(
    pointExisting ?? { ...defaultPoint, facility_id: facilityId }
  )
  const [pointType, setPointType] = useState<MaintenancePointType>(pointExisting?.type ?? 'waste_water_drain')

  // Seed only when the modal opens / switches entity — not when parent poll refreshes `pointExisting`.
  useEffect(() => {
    if (!isOpen) {
      return
    }
    const next = pointExisting ?? { ...defaultPoint, facility_id: facilityId }
    setTempPoint(next)
    setPointType(next.type ?? 'waste_water_drain')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: ignore poll identity changes of pointExisting
  }, [isOpen, pointExisting?.id, facilityId])

  const typeOptions: IOption[] = useMemo(
    () => MAINTENANCE_POINT_TYPE_OPTIONS.map(option => ({ value: option.value, text: option.text })),
    []
  )
  const typeOption = useMemo(() => typeOptions.find(option => option.value === pointType), [pointType, typeOptions])

  const { facilityGeoPoint, contextPins, robots, loading } = useFacilityDialogGeoContext(facilityId, isOpen, {
    excludeMaintenancePointId: isEditMode ? pointExisting?.id ?? null : null,
  })

  const mapPins = useMemo(() => {
    if (!tempPoint.point) {
      return []
    }
    return [
      {
        point: tempPoint.point,
        iconId: maintenancePointMapIconId(isEditMode ? tempPoint.type : pointType),
      },
    ]
  }, [isEditMode, pointType, tempPoint.point, tempPoint.type])

  const settledCameraKey = loading
    ? 'loading'
    : [
        facilityGeoPoint?.coordinates?.join(',') ?? 'nogeo',
        contextPins.map(pin => pin.id).join('|') || 'nopins',
      ].join(':')

  const onSaveClick = async () => {
    setSaving(true)
    try {
      if (!tempPoint.point) {
        throw new Error('Maintenance point coordinates are required')
      }
      if (pointExisting) {
        await MaintenancePointApi.patchMaintenancePoint(pointExisting.id, {
          name: tempPoint.name.trim(),
          point: tempPoint.point,
        })
      } else {
        await MaintenancePointApi.createMaintenancePoint({
          name: tempPoint.name.trim(),
          type: pointType,
          facility_id: facilityId,
          point: tempPoint.point,
        })
      }
      onSave()
    } catch (error) {
      console.warn('Error saving maintenance point', error)
    }
    setSaving(false)
  }

  const isButtonsDisabled = !tempPoint.point || !tempPoint.name?.trim() || (!isEditMode && !pointType)

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={style.root}
      shouldCloseOnOverlayClick={true}
      style={{ overlay: { zIndex: 100 } }}
      ariaHideApp={false}
    >
      <div className={style.titleBlock}>
        <div className={style.title}>{isEditMode ? 'Edit Maintenance Point' : 'New Maintenance Point'}</div>
      </div>
      <div className={style.rowAlignEnd}>
        <TextFieldDescribed
          className={style.textfield}
          description="Name"
          value={tempPoint.name}
          placeholder="Input point name"
          onChange={e => setTempPoint(prev => ({ ...prev, name: e.target.value }))}
        />
        <Dropdown
          className={style.textfield}
          description="Type"
          currentOption={typeOption}
          options={typeOptions}
          placeholder="Select type"
          onClickOption={(option: IOption) => setPointType(option.value as MaintenancePointType)}
          disabled={isEditMode}
        />
      </div>
      <AssetGeoPointMap
        className={style.map}
        mode={isEditMode ? 'edit' : 'create'}
        point={tempPoint.point}
        facilityPoint={facilityGeoPoint}
        pins={mapPins}
        contextPins={contextPins}
        contextRobots={robots}
        contextLoading={loading}
        onPointChanged={point => setTempPoint(prev => ({ ...prev, point }))}
        cameraResetKey={`${isOpen}-${isEditMode ? 'edit' : 'create'}-${pointExisting?.id ?? 0}-f${facilityId}:${settledCameraKey}`}
      />
      <div className={style.footer}>
        <Button.Outlined className={style.btn} onClick={onClose}>
          {t('common.cancel')}
        </Button.Outlined>
        <Button.Standart className={style.btn} disabled={saving || isButtonsDisabled} loading={saving} onClick={onSaveClick}>
          {t('common.save')}
        </Button.Standart>
      </div>
    </Modal>
  )
}

export default AddMaintenancePointModal
